import { type ReactElement } from 'react';
import { Button, Group, Select, TextInput } from '@mantine/core';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { Search, X } from 'lucide-react';
import type { DirectoryEligibleConnection } from './DirectoryApiService';

interface DirectoryToolbarProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    statusFilter: string;
    onStatusChange: (value: string) => void;
    departments: string[];
    departmentFilter: string;
    onDepartmentChange: (value: string) => void;
    connections: DirectoryEligibleConnection[];
    sourceFilter: string;
    onSourceChange: (value: string) => void;
    hasActiveFilters: boolean;
    onClear: () => void;
}

const STATUS_OPTIONS = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Suspended', value: 'suspended' },
    { label: 'Deprovisioned', value: 'deprovisioned' },
    { label: 'Staged', value: 'staged' },
    { label: 'Recovery', value: 'recovery' },
    { label: 'Unknown', value: 'unknown' },
];

export default function DirectoryToolbar({
    searchTerm,
    onSearchChange,
    statusFilter,
    onStatusChange,
    departments,
    departmentFilter,
    onDepartmentChange,
    connections,
    sourceFilter,
    onSourceChange,
    hasActiveFilters,
    onClear,
}: DirectoryToolbarProps): ReactElement {
    const isWide = useBreakpointUp('sm');
    return (
        <Group gap="sm" align="center" w={isWide ? undefined : '100%'}>
            <TextInput
                leftSection={<Search size={16} />}
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.currentTarget.value)}
                w={isWide ? 260 : '100%'}
                aria-label="Search directory"
            />
            <Select
                placeholder="Status"
                aria-label="Status"
                data={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(v) => onStatusChange(v ?? 'all')}
                w={isWide ? 160 : '100%'}
                allowDeselect={false}
            />
            <Select
                placeholder="Department"
                aria-label="Department"
                data={[{ label: 'All Departments', value: 'all' }, ...departments.map((d) => ({ label: d, value: d }))]}
                value={departmentFilter}
                onChange={(v) => onDepartmentChange(v ?? 'all')}
                w={isWide ? 200 : '100%'}
                allowDeselect={false}
                searchable
            />
            <Select
                placeholder="Directory Provider"
                aria-label="Directory Provider"
                data={[{ label: 'All Directory Providers', value: 'all' }, ...connections.map((c) => ({ label: c.displayName, value: c.id }))]}
                value={sourceFilter}
                onChange={(v) => onSourceChange(v ?? 'all')}
                w={isWide ? 220 : '100%'}
                allowDeselect={false}
            />
            {hasActiveFilters && (
                <Button variant="subtle" color="gray" rightSection={<X size={14} />} onClick={onClear}>
                    Clear
                </Button>
            )}
        </Group>
    );
}
